import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    FormControlLabel,
    Grid,
    Checkbox,
    Paper,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TableSortLabel,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from "./TailwindMuiStubs";
import { Edit as EditIcon, Save as SaveIcon } from "lucide-react";
import socket from "../../socket";
import { connectSocket } from "../../socket";
import { Typography } from "./TailwindMuiStubs";

const RegistrationAttendance = () => {
    const [crews, setCrews] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [presentFilter, setPresentFilter] = useState(""); // Filter for present status
    const { selectedYear } = useOutletContext(); // Global year from layout
    const [editingParticipant, setEditingParticipant] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" }); // Sorting configuration
    const [newCrew, setNewCrew] = useState({
        category_id: "",
        participants: [],
        wants_canoe: false,
    });
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState(""); // Search query for participant names
    const [paidFilter, setPaidFilter] = useState(""); // Filter for payment status
    const [localStartNumbers, setLocalStartNumbers] = useState({}); // Local state for start numbers

    useEffect(() => {

        connectSocket();
        // Fetch crews for the selected year when the component is rendered
        const fetchCrews = () => {
            socket.emit("fetchCrews", selectedYear);
        };

        fetchCrews(); // Trigger the fetchCrews event

        // Listen for crews data
        socket.on("crewsData", (data) => {
            setCrews(data);
        });

        // Fetch categories from the backend
        socket.emit("fetchCategories");
        socket.on("categoriesData", (data) => {
            setCategories(data);
        });

        // Listen for real-time updates
        socket.on("crewUpdated", (updatedCrew) => {
            setCrews((prevCrews) =>
                prevCrews.map((crew) => (crew.id === updatedCrew.id ? updatedCrew : crew))
            );
        });

        socket.on("error", (error) => {
            alert(error.message || "Nastala chyba pri komunikácii so serverom.");
        });

        return () => {
            socket.off("crewsData");
            socket.off("categoriesData");
            socket.off("crewUpdated");
            socket.off("error");
        };
    }, []); // Empty dependency array ensures this runs only once when the component is mounted

    useEffect(() => {
        connectSocket();

        // Fetch crews for the selected year
        const fetchCrews = () => {
            socket.emit("fetchCrews", selectedYear);
        };

        fetchCrews();

        // Listen for crews data
        socket.on("crewsData", (data) => {
            setCrews(data);
        });

        // Listen for real-time updates
        socket.on("crewUpdated", (updatedCrew) => {
            setCrews((prevCrews) =>
                prevCrews.map((crew) => (crew.id === updatedCrew.id ? updatedCrew : crew))
            );
        });

        return () => {
            socket.off("crewsData");
            socket.off("crewUpdated");
            socket.off("error");
        };
    }, [selectedYear]);

    const handleChange = (crewId, field, value) => {
        if (field === "present" && value) {
            const crew = crews.find((c) => c.id === crewId);
            if (!crew.start_number) {
                alert("Štartovacie číslo musí byť vyplnené pred označením prítomnosti.");
                return;
            }
        }

        if (field === "paid" && !value) {
            const confirmUncheck = window.confirm(
                "Naozaj chcete označiť túto posádku ako nezaplatenú?"
            );
            if (!confirmUncheck) {
                return; // Do not proceed if the user cancels
            }
        }

        setCrews((prevCrews) =>
            prevCrews.map((crew) =>
                crew.id === crewId ? { ...crew, [field]: value } : crew
            )
        );

        // Emit the change to the backend for real-time updates
        socket.emit("updateCrewField", { crewId, field, value });
    };

    const handleStartNumberChange = (crewId, value) => {
        // Allow only integers or an empty value
        if (/^\d*$/.test(value)) {
            setLocalStartNumbers((prev) => ({ ...prev, [crewId]: value }));

            // Emit the change to the backend for real-time updates
            socket.emit("updateCrewField", { crewId, field: "start_number", value });

            // If the start number is cleared, ensure "Prítomný" is unchecked
            if (!value) {
                setCrews((prevCrews) =>
                    prevCrews.map((crew) =>
                        crew.id === crewId ? { ...crew, present: false } : crew
                    )
                );

                // Emit the change to the backend to uncheck "Prítomný"
                socket.emit("updateCrewField", { crewId, field: "present", value: false });
            }
        }
    };

    const handleParticipantEdit = (participantId) => {
        setEditingParticipant(participantId);
    };

    const handleParticipantSave = (crewId, participantId) => {
        const crew = crews.find((crew) => crew.id === crewId);
        const updatedParticipant = crew.participants.find((p) => p.id === participantId);

        // Emit the updateParticipant event to the backend
        socket.emit("updateParticipant", { crewId, participant: updatedParticipant });

        setEditingParticipant(null);
    };

    const handleParticipantChange = (crewId, participantId, field, value) => {
        setCrews((prevCrews) =>
            prevCrews.map((crew) =>
                crew.id === crewId
                    ? {
                        ...crew,
                        participants: crew.participants.map((participant) =>
                            participant.id === participantId
                                ? { ...participant, [field]: value }
                                : participant
                        ),
                    }
                    : crew
            )
        );
    };

    const handleCategoryFilterChange = (event) => {
        setSelectedCategory(event.target.value);
    };

    const handlePresentFilterChange = (event) => {
        setPresentFilter(event.target.value);
    };

    const handleSort = (key) => {
        const direction =
            sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
        setSortConfig({ key, direction });
    };

    const handleNewCrewChange = (field, value) => {
        setNewCrew((prev) => ({ ...prev, [field]: value }));

        // Automatically populate participants based on category
        if (field === "category_id") {
            const selectedCategory = categories.find((cat) => cat.id === Number(value));
            if (selectedCategory) {
                const participantCount = selectedCategory.crew_size_max || 1; // Default to 1 participant if not specified
                const participants = Array.from({ length: participantCount }, () => ({
                    name: "",
                    birth_date: "",
                    city: "",
                }));
                setNewCrew((prev) => ({
                    ...prev,
                    participants,
                    wants_canoe: false, // Reset canoe rental when category changes
                }));
            }
        }
    };

    const handleNewParticipantChange = (index, field, value) => {
        const updatedParticipants = [...newCrew.participants];
        updatedParticipants[index][field] = value;
        setNewCrew((prev) => ({ ...prev, participants: updatedParticipants }));
    };

    const handleRegisterNewCrew = () => {
        // Emit the new crew registration event to the backend
        socket.emit("registerNewCrew", { ...newCrew, marathon_year: selectedYear });

        // Reset the form and close the popup
        setNewCrew({
            category_id: "",
            participants: [],
            wants_canoe: false,
        });
        setIsPopupOpen(false);
    };

    const handleSearchQueryChange = (event) => {
        setSearchQuery(event.target.value.toLowerCase());
    };

    const handlePaidFilterChange = (event) => {
        setPaidFilter(event.target.value);
    };

    const sortedCrews = [...crews].sort((a, b) => {
        if (!sortConfig.key) return 0;

        let aValue, bValue;

        switch (sortConfig.key) {
            case "present":
            case "paid":
                // Sort boolean values: false < true
                aValue = Boolean(a[sortConfig.key]);
                bValue = Boolean(b[sortConfig.key]);
                break;
            case "category":
                // Sort by category name alphabetically
                aValue = a.category?.name?.toLowerCase() || "";
                bValue = b.category?.name?.toLowerCase() || "";
                break;
            case "start_number":
                // Sort numerically by start number
                aValue = parseInt(a.start_number, 10) || 0;
                bValue = parseInt(b.start_number, 10) || 0;
                break;
            default:
                // Default sorting for other fields
                aValue = a[sortConfig.key];
                bValue = b[sortConfig.key];
        }

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
    });

    const filteredCrews = sortedCrews.filter((crew) => {
        const categoryMatch = selectedCategory === ""
            ? true
            : String(crew.category?.id) === String(selectedCategory);

        const presentMatch = presentFilter === ""
            ? true
            : presentFilter === "present"
                ? !!crew.present
                : !crew.present;

        const paidMatch = paidFilter === ""
            ? true
            : paidFilter === "paid"
                ? !!crew.invoice?.paid
                : !crew.invoice?.paid;

        const searchMatch = searchQuery === ""
            ? true
            : crew.participants?.some((participant) =>
                participant.name?.toLowerCase().includes(searchQuery)
            ) ?? false;

        return categoryMatch && presentMatch && paidMatch && searchMatch;
    });

    const selectedCategoryDetails = categories.find(
        (category) => category.id === newCrew.category_id
    );

    return (
        <div>
            <div className="mb-6">
                <Typography variant="h4">Registrácia a Prezenčka</Typography>
                <Typography variant="body2">
                    Kompletná tabuľka registrovaných posádok. Rýchle označovanie prítomnosti, štartovacích čísel a úhrad.
                </Typography>
            </div>

            {/* Filters */}
            <Grid
                container
                spacing={2}
                sx={{
                    marginBottom: 4,
                    zIndex: 10,
                    backgroundColor: "white",
                    paddingBottom: 2,
                    borderBottom: "1px solid #f3f4f6",
                }}
            >
                <Grid item sx={{ display: "flex", alignItems: "center" }} >

                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Prítomnosť</InputLabel>
                        <Select
                            value={presentFilter}
                            onChange={handlePresentFilterChange}
                        >
                            <MenuItem value="">Všetci</MenuItem>
                            <MenuItem value="present">Prítomní</MenuItem>
                            <MenuItem value="absent">Neprítomní</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item >
                    <FormControl sx={{ minWidth: 220 }}>
                        <InputLabel>Kategória</InputLabel>
                        <Select
                            value={selectedCategory}
                            onChange={handleCategoryFilterChange}
                        >
                            <MenuItem value="">Všetky</MenuItem>
                            {categories.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                    {category.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item sx={{ flex: 1 }}>
                    <TextField
                        label="Hľadať účastníka"
                        placeholder="Zadajte meno..."
                        value={searchQuery}
                        onChange={handleSearchQueryChange}
                        fullWidth
                    />
                </Grid>
                <Grid item >
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Platba</InputLabel>
                        <Select
                            value={paidFilter}
                            onChange={handlePaidFilterChange}
                        >
                            <MenuItem value="">Všetky</MenuItem>
                            <MenuItem value="paid">Zaplatené</MenuItem>
                            <MenuItem value="unpaid">Nezaplatené</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item sx={{ display: "flex", alignItems: "center", marginLeft: "auto" }} >
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setIsPopupOpen(true)}
                        sx={{ marginBottom: 2, marginLeft: "auto" }}
                        height="100%"
                    >
                        Registrovať novú posádku
                    </Button>
                </Grid>
            </Grid>

            {/* New Crew Registration Popup */}
            <Dialog open={isPopupOpen} onClose={() => setIsPopupOpen(false)}>
                <DialogTitle>Registrovať novú posádku</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ marginBottom: 2 }}>
                        <InputLabel>Kategória</InputLabel>
                        <Select
                            value={newCrew.category_id}
                            onChange={(e) => handleNewCrewChange("category_id", e.target.value)}
                        >
                            {categories.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                    {category.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {selectedCategoryDetails?.rental_available && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={newCrew.wants_canoe}
                                    onChange={(e) =>
                                        setNewCrew((prev) => ({
                                            ...prev,
                                            wants_canoe: e.target.checked,
                                        }))
                                    }
                                />
                            }
                            label="Prenájom kanoe"
                            sx={{ marginTop: 2 }}
                        />
                    )}
                    <h4>Účastníci</h4>
                    {newCrew.participants.map((participant, index) => (
                        <Grid container spacing={2} key={index} sx={{ marginBottom: 2 }}>
                            <Grid xs={12} sm={4} item>
                                <TextField
                                    label="Meno"
                                    value={participant.name}
                                    onChange={(e) =>
                                        handleNewParticipantChange(index, "name", e.target.value)
                                    }
                                    fullWidth
                                />
                            </Grid>
                            <Grid xs={12} sm={4} item>
                                <TextField
                                    label="Dátum narodenia"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    value={participant.birth_date}
                                    onChange={(e) =>
                                        handleNewParticipantChange(index, "birth_date", e.target.value)
                                    }
                                    fullWidth
                                />
                            </Grid>
                            <Grid xs={12} sm={4} item>
                                <TextField
                                    label="Mesto"
                                    value={participant.city}
                                    onChange={(e) =>
                                        handleNewParticipantChange(index, "city", e.target.value)
                                    }
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsPopupOpen(false)}>Zrušiť</Button>
                    <Button variant="contained" color="primary" onClick={handleRegisterNewCrew}>
                        Registrovať
                    </Button>
                </DialogActions>
            </Dialog>

            <TableContainer
                component={Paper}
                sx={{
                    overflow: "visible", // Disable nested scroll
                }}
            >
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <TableSortLabel
                                    active={sortConfig.key === "present"}
                                    direction={sortConfig.direction}
                                    onClick={() => handleSort("present")}
                                >
                                    Prítomný
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortConfig.key === "category"}
                                    direction={sortConfig.direction}
                                    onClick={() => handleSort("category")}
                                >
                                    Kategória
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>Mená účastníkov</TableCell>
                            <TableCell>Zapožičať kanoe</TableCell>
                            <TableCell>Celková cena</TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortConfig.key === "paid"}
                                    direction={sortConfig.direction}
                                    onClick={() => handleSort("paid")}
                                >
                                    Zaplatené
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortConfig.key === "start_number"}
                                    direction={sortConfig.direction}
                                    onClick={() => handleSort("start_number")}
                                >
                                    Štartovacie číslo
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>Poznámka</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredCrews.map((crew) => (
                            <TableRow key={crew.id}>
                                <TableCell>
                                    <Checkbox
                                        checked={crew.present || false}
                                        onChange={(e) =>
                                            handleChange(crew.id, "present", e.target.checked)
                                        }
                                        disabled={!crew.start_number} // Disable only if no start number
                                    />
                                </TableCell>
                                <TableCell>
                                    <Select
                                        value={crew.category?.id || ""}
                                        onChange={(e) =>
                                            handleChange(crew.id, "category_id", e.target.value)
                                        }
                                        disabled={crew.present}
                                        size="small"
                                        sx={{ minWidth: 150 }}
                                        variant="outlined"
                                    >
                                        {categories.map((cat) => (
                                            <MenuItem key={cat.id} value={cat.id}>
                                                {cat.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </TableCell>
                                <TableCell>
                                    {crew.participants.map((participant) => (
                                        <div key={participant.id} className="flex items-center gap-2 mb-1 group max-w-xs">
                                            {editingParticipant === participant.id ? (
                                                <div className="flex items-center gap-2 w-full">
                                                    <TextField
                                                        value={participant.name}
                                                        onChange={(e) =>
                                                            handleParticipantChange(
                                                                crew.id,
                                                                participant.id,
                                                                "name",
                                                                e.target.value
                                                            )
                                                        }
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") {
                                                                handleParticipantSave(crew.id, participant.id);
                                                            }
                                                        }}
                                                        disabled={crew.present} // Disable if present
                                                    />
                                                    <IconButton
                                                        color="primary"
                                                        size="small"
                                                        onClick={() =>
                                                            handleParticipantSave(
                                                                crew.id,
                                                                participant.id
                                                            )
                                                        }
                                                        disabled={crew.present} // Disable if present
                                                    >
                                                        <SaveIcon size={18} />
                                                    </IconButton>
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-between w-full p-1.5 rounded-lg hover:bg-blue-50/50 transition-colors border border-transparent hover:border-blue-100">
                                                    <span className="font-semibold text-gray-800">{participant.name}</span>
                                                    <button
                                                        className={`p-1.5 rounded-md transition-all ${crew.present ? 'opacity-30 cursor-not-allowed' : 'text-[#1E3A8A] hover:bg-blue-100 opacity-60 hover:opacity-100 group-hover:opacity-100 hover:shadow-sm'}`}
                                                        onClick={() =>
                                                            !crew.present && handleParticipantEdit(participant.id)
                                                        }
                                                        disabled={crew.present} // Disable if present
                                                        title="Upraviť účastníka"
                                                    >
                                                        <EditIcon size={14} strokeWidth={3} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </TableCell>
                                <TableCell>
                                    <Checkbox checked={crew.wants_canoe} disabled />
                                </TableCell>
                                <TableCell>
                                    {crew.invoice?.total_price || "N/A"} € {/* Display total price */}
                                </TableCell>
                                <TableCell>
                                    <Checkbox
                                        checked={crew.invoice?.paid || false}
                                        onChange={(e) =>
                                            handleChange(crew.id, "paid", e.target.checked)
                                        }
                                        disabled={crew.present} // Disable if present
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        value={(localStartNumbers[crew.id] ?? crew.start_number) || ""}
                                        onChange={(e) =>
                                            handleStartNumberChange(crew.id, e.target.value)
                                        }
                                        disabled={crew.present} // Disable if present
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        value={crew.admin_note || ""}
                                        onChange={(e) =>
                                            handleChange(crew.id, "admin_note", e.target.value)
                                        }
                                        disabled={crew.present} // Disable if present
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default RegistrationAttendance;
